import React from 'react';
import clsx from 'clsx';
import {
	AssetGalleryBreadcrumbsCategory,
	type AssetGalleryBreadcrumbsCategoryProps,
} from '@netx/core-asset-gallery/components/AssetGalleryBreadcrumbs/AssetGalleryBreadcrumbsCategory';
import ScrollBorder from '@netx/core-components/ScrollBorder';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import {
	CategoryCardsContainer,
	type CategoryCardsContainerProps,
} from '../CategoryCards/CategoryCardsContainer';
import {
	CATEGORY_GALLERY_NAME,
	categoryGalleryClasses,
	type CategoryGalleryClasses,
	getCategoryGalleryUtilityClass,
} from './categoryGalleryClasses';

type CategoryGalleryOwnerState = WithClasses<CategoryGalleryClasses>;

const useUtilityClasses = (ownerState: CategoryGalleryOwnerState) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
		breadcrumbs: ['breadcrumbs'],
		content: ['content'],
	};
	return composeClasses(slots, getCategoryGalleryUtilityClass, classes);
};

const CategoryGalleryRoot = styled('div', {
	name: CATEGORY_GALLERY_NAME,
	slot: 'Root',
})(({theme}) => ({
	display: 'flex',
	flexDirection: 'column',
	flex: '1 1 auto',
	overflow: 'hidden',
}));

const CategoryGalleryContent = styled(ScrollBorder, {
	name: CATEGORY_GALLERY_NAME,
	slot: 'Content',
})(({theme}) => ({}));

const AssetGalleryBreadcrumbsRoot = styled(AssetGalleryBreadcrumbsCategory, {
	name: CATEGORY_GALLERY_NAME,
	slot: 'Breadcrumbs',
})(({theme}) => ({
	width: '100%',
	padding: theme.spacing(1, 2),
}));

interface CategoryGalleryProps
	extends WithClasses<
		CategoryGalleryClasses,
		React.DetailedHTMLProps<
			React.HTMLAttributes<HTMLDivElement>,
			HTMLDivElement
		>
	> {
	categoryId: number;
	slotProps?: {
		breadcrumbs?: Partial<Omit<AssetGalleryBreadcrumbsCategoryProps, 'id'>>;
		content?: React.HTMLAttributes<HTMLDivElement>;
		cardsContainer?: Partial<Omit<CategoryCardsContainerProps, 'id'>>;
	};
}

export const CategoryGallery = ({
	classes: _classes,
	className,
	categoryId,
	slotProps,
	...props
}: CategoryGalleryProps) => {
	const ownerState: CategoryGalleryOwnerState = {
		classes: _classes,
		className,
	};
	const classes = useUtilityClasses(ownerState);

	return (
		<CategoryGalleryRoot
			{...props}
			className={clsx(classes.root, className)}
			ownerState={ownerState}
		>
			<AssetGalleryBreadcrumbsRoot
				{...slotProps?.breadcrumbs}
				id={categoryId}
			/>
			<CategoryGalleryContent
				{...slotProps?.content}
				className={classes.content}
			>
				<CategoryCardsContainer
					{...slotProps?.cardsContainer}
					id={categoryId}
				/>
			</CategoryGalleryContent>
		</CategoryGalleryRoot>
	);
};
